import React, { useState, useEffect } from 'react'
import {
    Box,
    Flex,
    Icon,
    Text,
    Button,
    Link,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl, FormattedMessage } from 'react-intl'

/* Styles */
import { useStoreLocatorStyles } from '../styles/storeLocatorStyles'

/* Form fields */
import { set, useForm } from 'react-hook-form'
import StoreLocatorFields from '../../forms/store-locator-fields'

/* Context */
import { useStoreLocator } from '../../../contexts/store-locator-context';
import { useBrand } from '../../../contexts/brand-context'

/* Icons */
import { AlertTriangleIcon, GeneralSearchIcon, Geolocation } from '../../icons'

/* Hooks */
import { useSearchStores } from '@salesforce/commerce-sdk-react'
import { useAddressGeolocation } from '../../../integrations/address-geolocation/hooks/use-address-geolocation'

/* Configs */
import { STORE_LOCATOR_CONFIGS, getLocaleDefaults, detectCountryFromSearch } from '../configs'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const StoreLocatorInput = () => {
    /* Styling */
    const styles = useStoreLocatorStyles()
    const inputStyling = styles?.components?.inputStyling

    const intl = useIntl()
    const {brandId} = useBrand()
    const localeDefaults = getLocaleDefaults(intl.locale)
    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const form = useForm({
        mode: 'onSubmit'
    })

    const {
        stores,
        setStores,
        setSearchValue,
        searchValue,
        getLocation,
        permissionStatus,
        latitude,
        longitude,
        noSearchResults,
        setNoSearchResults,
        setIsStoreDataLoading,
        setLatitude,
        setLongitude,
        setIsGeolocationSearch,
        isGeolocationSearch,
        showGeoError,
        setShowGeoError,
        setSelectedStore,
        setActiveMarkerId,
        shouldUseDefaultMapViewport,
        setShouldUseDefaultMapViewport
    } = useStoreLocator()

    const [searchType, setSearchType] = useState('initial')
    const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false)
    const [offset, setOffset] = useState(0)
    const [address, setAddress] = useState(null)
    const [isFallback, setIsFallback] = useState(false)
    const [countryOverride, setCountryOverride] = useState(null)

    const isCountrySearch = searchType === 'country_search'
    const isAllStoresSearch = searchType === 'all_stores'
    const defaultLatitude = localeDefaults.defaultCityLocation.latitude
    const defaultLongitude = localeDefaults.defaultCityLocation.longitude
    const activeCountryCode = countryOverride?.countryCode || localeDefaults.countryCode

    const {
        data: searchStoresData,
        isLoading: isStoreDataLoading
    } = useSearchStores({
        parameters: {
            countryCode: activeCountryCode,
            latitude: latitude,
            longitude: longitude,
            locale: intl.locale,
            ...((permissionStatus === 'granted' || searchValue) && !isCountrySearch && !isAllStoresSearch && { maxDistance: getSiteEnvVarValue(config.app, 'STORELOCATOR_SEARCH_RADIUS', siteId) || STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE }),
            limit: STORE_LOCATOR_CONFIGS.NUM_STORES_PER_REQUEST_API_MAX,
            ...(offset !== -1 && { offset: offset }),
            distanceUnit: getSiteEnvVarValue(config.app, 'STORELOCATOR_DISTANCE_UNIT', siteId) || STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE_UNIT,
            c_brand: brandId
        }
    }, {
        enabled: (!!latitude && !!longitude)
    })

    const { location } = useAddressGeolocation(address)

    // Initial load - try geolocation first
    useEffect(() => {
        if (permissionStatus === 'granted') {
            setShowGeoError(false)
            if (searchType === 'initial' || searchType === 'geolocation') {
                setShouldUseDefaultMapViewport(false)
            }
        }

        if (!hasAttemptedInitialLoad && permissionStatus === null) {
            setHasAttemptedInitialLoad(true)
            setSearchType('initial')
            getLocation()
        }
    }, [hasAttemptedInitialLoad, permissionStatus, getLocation, searchType])

    useEffect(() => {
        if (location) {
            if (!location?.success && STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.error('❌ There is an error on the geolocation API')
            }

            if (location?.success) {
                setSearchType('manual_search')
                setLatitude(location?.lat)
                setLongitude(location?.lng)
                setOffset(-1)
                setSearchValue(address || null)
                setShouldUseDefaultMapViewport(false)
            } else {
                setSearchType('manual_search_invalid_address')
                setSearchValue(address || null)
            }
        }
    }, [location])

    // Handle geolocation permission status changes
    useEffect(() => {
        if ((permissionStatus !== 'granted' || permissionStatus === 'denied') && searchType === 'initial') {
            if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.log('📄 Initial load: Geolocation denied, using default location')
            }
            setLatitude(localeDefaults.defaultCityLocation.latitude)
            setLongitude(localeDefaults.defaultCityLocation.longitude)
            setShouldUseDefaultMapViewport(true)
        }
    }, [permissionStatus, searchType])

    const onSubmit = async (data) => {
        const rawInput = data?.storeLocatorSearch
        const normalizedInput = typeof rawInput === 'string' ? rawInput.trim() : ''
        const isAlreadyShowingDefaultStores = (
            !searchValue &&
            !countryOverride &&
            !address &&
            !isFallback &&
            !noSearchResults &&
            !isGeolocationSearch &&
            shouldUseDefaultMapViewport &&
            latitude === defaultLatitude &&
            longitude === defaultLongitude
        )
        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
            console.log('📄 Manual search initiated for:', rawInput)
        }

        if (!normalizedInput && isAlreadyShowingDefaultStores) {
            return
        }

        setSelectedStore(null)
        setActiveMarkerId(null)
        setShowGeoError(false)

        if (!normalizedInput) {
            setSearchType('all_stores')
            setCountryOverride(null)
            setIsFallback(false)
            setNoSearchResults(false)
            setStores([])
            setOffset(0)
            setSearchValue(null)
            setAddress(null)
            setLatitude(defaultLatitude)
            setLongitude(defaultLongitude)
            setIsGeolocationSearch(false)
            setShouldUseDefaultMapViewport(true)
            return
        }

        // If the user typed a supported country name, skip geolocation and
        // search directly for all stores in that country (no maxDistance).
        const matchedCountry = detectCountryFromSearch(normalizedInput)
        if (matchedCountry) {
            if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.log('📄 Country search detected:', matchedCountry.countryCode)
            }
            setCountryOverride({ countryCode: matchedCountry.countryCode })
            setSearchType('country_search')
            setIsFallback(false)
            setNoSearchResults(false)
            setOffset(0)
            setSearchValue(normalizedInput)
            setAddress(null)
            setLatitude(matchedCountry.defaultCityLocation.latitude)
            setLongitude(matchedCountry.defaultCityLocation.longitude)
            setShouldUseDefaultMapViewport(false)
            return
        }

        // Default flow: geocode the address first.
        setCountryOverride(null)
        setShouldUseDefaultMapViewport(false)
        setAddress(normalizedInput)
    }

    const handleGeolocation = () => {
        if (permissionStatus === 'denied') {
            setShowGeoError(true)
            return
        }

        setShowGeoError(false)
        setSearchValue(null)
        setSearchType('geolocation')
        setSelectedStore(null)
        setActiveMarkerId(null)
        setCountryOverride(null)
        setShouldUseDefaultMapViewport(false)
        getLocation()
    }

    useEffect(() => {
        setIsStoreDataLoading(isStoreDataLoading)
    }, [isStoreDataLoading])

    // Hook to populate all stores if the permission was not granted
    useEffect(() => {
        if (searchStoresData && (permissionStatus !== 'granted' || isAllStoresSearch) && offset !== -1 && searchType !== 'country_search') {
            const apiLimit = searchStoresData?.limit
            const apiTotalStores = searchStoresData?.total
            const apiOffset = searchStoresData?.offset

            setStores([...stores, ...searchStoresData?.data])
            setOffset((apiOffset + apiLimit) < apiTotalStores ? apiOffset + apiLimit : apiOffset)

            if ((apiOffset + apiLimit) === apiTotalStores) {
                setOffset(-1)
            }
        }
    }, [searchStoresData, offset])

    // Hook to paginate through all pages during a country search and
    // accumulate only the stores matching the selected country code.
    useEffect(() => {
        if (!searchStoresData || searchType !== 'country_search' || offset === -1) return

        const apiLimit = searchStoresData?.limit || 0
        const apiTotalStores = searchStoresData?.total || 0
        const apiOffset = searchStoresData?.offset || 0
        const pageData = searchStoresData?.data || []

        const pageFiltered = pageData.filter(
            (store) => store?.countryCode === activeCountryCode
        )

        // Accumulate across pages. On the first page (apiOffset === 0),
        // replace the list; on subsequent pages, append.
        setStores((prev) => (apiOffset === 0 ? pageFiltered : [...prev, ...pageFiltered]))

        const nextOffset = apiOffset + apiLimit
        if (nextOffset < apiTotalStores) {
            setOffset(nextOffset)
        } else {
            setOffset(-1)
            if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.log(`📄 Country search: Finished paginating ${apiTotalStores} results for ${activeCountryCode}`)
            }
        }
    }, [searchStoresData])

    // Hook to handle store search results
    useEffect(() => {
        if (!searchStoresData) return

        const hasStores = searchStoresData?.data && searchStoresData.data.length > 0

        switch (searchType) {
            case 'initial':
                if (isGeolocationSearch) {
                    if (hasStores) {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Initial geolocation: Found stores')
                        }
                        setStores(searchStoresData.data)
                        setNoSearchResults(false)
                        setIsGeolocationSearch(false)
                        setIsFallback(false)
                        setShouldUseDefaultMapViewport(false)
                    } else {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Initial geolocation: No stores found, falling back to default location')
                        }
                        setSearchType('fallback')
                        setLatitude(localeDefaults.defaultCityLocation.latitude)
                        setLongitude(localeDefaults.defaultCityLocation.longitude)
                        setIsGeolocationSearch(false)
                        setIsFallback(true)
                        setShouldUseDefaultMapViewport(true)
                    }
                }
                break

            case 'geolocation':
                if (isGeolocationSearch) {
                    if (hasStores) {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Geolocation button: Found stores')
                        }
                        setStores(searchStoresData.data)
                        setNoSearchResults(false)
                        setIsGeolocationSearch(false)
                        setIsFallback(false)
                        setShouldUseDefaultMapViewport(false)
                    } else {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Geolocation button: No stores found, falling back to default location')
                        }
                        setSearchType('fallback')
                        setLatitude(localeDefaults.defaultCityLocation.latitude)
                        setLongitude(localeDefaults.defaultCityLocation.longitude)
                        setIsGeolocationSearch(false)
                        setIsFallback(true)
                        setShouldUseDefaultMapViewport(true)
                    }
                }
                break

            case 'manual_search':
                if (hasStores) {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Manual search: Found stores')
                    }
                    setStores(searchStoresData.data)
                    setNoSearchResults(false)
                    setIsFallback(false)
                    setShouldUseDefaultMapViewport(false)
                } else {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Manual search: No stores found, falling back to default location')
                    }
                    setSearchType('fallback')
                    setLatitude(localeDefaults.defaultCityLocation.latitude)
                    setLongitude(localeDefaults.defaultCityLocation.longitude)
                    setIsFallback(true)
                    setShouldUseDefaultMapViewport(true)
                }
                break
            case 'manual_search_invalid_address':
                if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                    console.log('📄 Manual search invalid address: Invalid address')
                }

                setStores([])
                setNoSearchResults(true)
                setLatitude(null)
                setLongitude(null)
                setShouldUseDefaultMapViewport(true)
                break
            case 'country_search':
                // Store accumulation is handled by the country-search
                // pagination effect above. Here we only update the
                // "no results" flag once we've finished paginating
                // (offset === -1) so the UI reflects the final state.
                if (offset === -1) {
                    const finalHasStores = hasStores && searchStoresData.data.some(
                        (store) => store?.countryCode === activeCountryCode
                    )
                    setNoSearchResults(!finalHasStores && stores.length === 0)
                    setIsFallback(false)
                    setShouldUseDefaultMapViewport(false)
                }
                break
            case 'all_stores':
                setNoSearchResults(false)
                setIsFallback(false)
                setShouldUseDefaultMapViewport(true)
                break
            case 'fallback':
                if (hasStores) {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Fallback to default location: Found stores')
                    }
                    setStores(searchStoresData.data)
                    setNoSearchResults(true) // Show no results message even if we found stores at fallback location
                    setIsFallback(true)
                    setShouldUseDefaultMapViewport(true)
                } else {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Fallback to default location: No stores found')
                    }
                    setStores([])
                    setNoSearchResults(true)
                    setIsFallback(true)
                    setShouldUseDefaultMapViewport(true)
                }
                // Reset search type after processing fallback
                setSearchType('completed')
                break

            default:
                // For any other cases or completed state, just update stores if needed
                if (hasStores && stores.length === 0) {
                    setStores(searchStoresData.data)
                    setNoSearchResults(false)
                    setShouldUseDefaultMapViewport(false)
                }
                break
        }
    }, [searchStoresData, searchType, isGeolocationSearch, permissionStatus])

    return (
        <Box>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <Box {...styles.inputWrapper} pb={isMobile && (noSearchResults || showGeoError) ? 0 : undefined}>
                    <Box {...inputStyling.container}>
                        <StoreLocatorFields form={form} {...inputStyling.inputField} />
                        <GeneralSearchIcon {...inputStyling.icon} />
                    </Box>
                    <Box {...inputStyling.results}>
                        {searchValue ? (
                            <Text {...inputStyling.stores}>
                                {isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.store_near'
                                        defaultMessage='{noOfStores} store near "{searchValue}"'
                                        values={{ noOfStores: noSearchResults ? 0 : (stores?.length || 0), searchValue: searchValue }}
                                    />}
                                {stores?.length > 0 && !isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.stores_near'
                                        defaultMessage='{noOfStores} stores near "{searchValue}"'
                                        values={{ noOfStores: stores?.length, searchValue: searchValue }}
                                    />}
                                {stores?.length === 0 && !isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.stores_near'
                                        defaultMessage='{noOfStores} stores near "{searchValue}"'
                                        values={{ noOfStores: stores?.length, searchValue: searchValue }}
                                    />}
                            </Text>
                        ) : (
                            <Text {...inputStyling.stores}>
                                <FormattedMessage
                                    id='store_locator.input.stores'
                                    defaultMessage="{noOfStores} stores"
                                    values={{ noOfStores: noSearchResults ? 0 : (stores?.length || 0) || 0 }}
                                />
                            </Text>
                        )}
                        <Box
                            as={Link}
                            variant={'unstyled'}
                            href={'#'}
                            {...inputStyling.geolocation}
                            onClick={handleGeolocation}
                            onKeyDown={(e) => {
                                if (e && e.key === 'Enter') {
                                    e.preventDefault()
                                    handleGeolocation(e)
                                }
                            }}>
                            <Geolocation {...inputStyling.geoIcon} />
                            <FormattedMessage
                                id='store_locator.input.geolocation'
                                defaultMessage="Use Current Location"
                            />
                        </Box>
                    </Box>
                    {showGeoError &&
                        <Flex {...inputStyling.geoErrorContainer} mb={noSearchResults ? '16px' : (isMobile ? '20px' : undefined)}>
                            <Flex alignItems="center" gap={2}>
                                <Icon as={AlertTriangleIcon} boxSize="20px" />
                                <Text {...inputStyling.geoErrorTitle}>
                                    <FormattedMessage
                                        id='store_locator.input.geo_error_title'
                                        defaultMessage='Location Access Blocked'
                                    />
                                </Text>
                            </Flex>
                            <Text {...inputStyling.geoErrorDescription}>
                                <FormattedMessage
                                    id='store_locator.input.geo_error'
                                    defaultMessage="Looks like you've blocked location access. Please enable it in your browser settings."
                                />
                            </Text>
                        </Flex>
                    }
                </Box>
            </form>
        </Box>
    )
}

export default StoreLocatorInput
